import { DateTime } from 'luxon';
import {
  DateTimeInput,
  parseDateTime,
  parseInZone,
  resolveDateTimeZone,
} from 'utils/dateParsing';

/*********** Types and Options ***********/

/**
 * Display formatter options for user-facing date labels.
 */
export type DateDisplayOptions = {
  /** Site or target timezone for display. Defaults to Luxon's local zone. */
  timezone?: string | null;
  /** Returned when the input cannot be parsed. Defaults to an empty string. */
  fallback?: string;
  /** Intl preset/options or a Luxon token string such as `yyyy-MM-dd HH:mm`. */
  format?: Intl.DateTimeFormatOptions | string;
  /** Append a short timezone label such as `CDT` where the environment supports it. */
  showTimeZone?: boolean;
};

// A subset of display options for patterns that do not support format overrides
// used by `formatDateTimePattern`
export type DatePatternOptions = Omit<DateDisplayOptions, 'format'>;

type NormalizedDateDisplayOptions = {
  timezone: string;
  hasExplicitTimezone: boolean;
  fallback: string;
  format?: Intl.DateTimeFormatOptions | string;
  showTimeZone: boolean;
};

const DATE_TIME_INPUT_FORMAT = 'yyyy-MM-dd HH:mm:ss';
const DATE_TIME_INPUT_PARSE_FORMATS = [
  DATE_TIME_INPUT_FORMAT,
  'yyyy-MM-dd HH:mm',
  'yyyy-MM-dd hh:mm a',
  'yyyy-MM-dd h:mm a',
  'yyyy MM dd hh:mm a',
] as const;

const DATE_ONLY_INPUT_FORMATS = ['yyyy-MM-dd', 'yyyy MM dd'] as const;
const DATE_TIME_EDITOR_FORMAT = 'yyyy-MM-dd hh:mm a';

const isDisplayOptions = (
  value: DateDisplayOptions | string | null | undefined
): value is DateDisplayOptions => typeof value === 'object' && value !== null;

const normalizeDisplayOptions = (
  optionsOrTimezone?: DateDisplayOptions | string | null,
  defaults: {
    fallback?: string;
    format?: Intl.DateTimeFormatOptions;
  } = {}
): NormalizedDateDisplayOptions => {
  const options = isDisplayOptions(optionsOrTimezone)
    ? optionsOrTimezone
    : { timezone: optionsOrTimezone };

  return {
    timezone: resolveDateTimeZone(options.timezone),
    hasExplicitTimezone: Boolean(options.timezone),
    fallback: options.fallback ?? defaults.fallback ?? '',
    format: options.format ?? defaults.format,
    showTimeZone: options.showTimeZone ?? false,
  };
};

const getDisplayFormat = (options: NormalizedDateDisplayOptions) => {
  const format =
    typeof options.format === 'string' ? undefined : options.format;

  return options.showTimeZone
    ? {
        ...format,
        timeZoneName: format?.timeZoneName ?? 'short',
      }
    : format;
};

const appendTimeZoneLabel = (
  label: string,
  parsed: DateTime,
  options: NormalizedDateDisplayOptions
) => {
  if (!options.showTimeZone) return label;

  const timeZoneLabel = parsed.offsetNameShort;
  return timeZoneLabel ? `${label} ${timeZoneLabel}` : label;
};

const getDisplayTimeZone = (
  value: DateTimeInput,
  options: NormalizedDateDisplayOptions
) =>
  !options.hasExplicitTimezone && DateTime.isDateTime(value) && value.zoneName
    ? value.zoneName
    : options.timezone;

const formatDateTimeValue = (
  value: DateTimeInput,
  optionsOrTimezone: DateDisplayOptions | string | null | undefined,
  formatter: (
    parsed: DateTime,
    options: NormalizedDateDisplayOptions
  ) => string | null,
  settings: {
    defaultFormat?: Intl.DateTimeFormatOptions;
    parseInTargetZone?: boolean;
  } = {}
) => {
  const options = normalizeDisplayOptions(optionsOrTimezone, {
    format: settings.defaultFormat,
  });
  const timezone = getDisplayTimeZone(value, options);
  const displayOptions = { ...options, timezone };
  const parsed = settings.parseInTargetZone
    ? parseInZone(value, timezone)
    : parseDateTime(value);

  return parsed.isValid
    ? formatter(parsed.setZone(timezone), displayOptions) ??
        displayOptions.fallback
    : displayOptions.fallback;
};

const formatWithLocale = (
  value: DateTimeInput,
  defaultFormat: Intl.DateTimeFormatOptions,
  optionsOrTimezone?: DateDisplayOptions | string | null,
  settings: { parseInTargetZone?: boolean } = {}
) =>
  formatDateTimeValue(
    value,
    optionsOrTimezone,
    (parsed, options) =>
      typeof options.format === 'string'
        ? appendTimeZoneLabel(parsed.toFormat(options.format), parsed, options)
        : parsed.toLocaleString(getDisplayFormat(options)),
    {
      defaultFormat,
      parseInTargetZone: settings.parseInTargetZone,
    }
  );

/**
 * Formats a calendar date for user-facing labels. Use for service dates,
 * statement dates, dashboard labels, and other date-only display text.
 *
 * Date-only strings are parsed in the target timezone to avoid off-by-one-day
 * shifts.
 */
export const formatDateOnly = (
  value: DateTimeInput,
  optionsOrTimezone?: DateDisplayOptions | string | null
) =>
  formatWithLocale(value, DateTime.DATE_MED, optionsOrTimezone, {
    parseInTargetZone: true,
  });

/**
 * Formats only the time portion for user-facing labels in the resolved timezone.
 * Use for clocks, timeline detail, and timestamp-adjacent time labels.
 */
export const formatTimeOnly = (
  value: DateTimeInput,
  optionsOrTimezone?: DateDisplayOptions | string | null
) => formatWithLocale(value, DateTime.TIME_SIMPLE, optionsOrTimezone);

/**
 * Formats a full date-time for user-facing labels in the resolved timezone.
 * Use for `createdAt`, `submittedAt`, claim history, and audit timestamps.
 */
export const formatDateTime = (
  value: DateTimeInput,
  optionsOrTimezone?: DateDisplayOptions | string | null
) => formatWithLocale(value, DateTime.DATETIME_MED, optionsOrTimezone);

/**
 * Formats a full timestamp for the Mantine DateTimePicker value used in modals.
 * Use at the picker boundary instead of repeating the picker format in components.
 */
export const formatDateTimeInputValue = (
  value: DateTimeInput,
  timezone?: string | null
) =>
  formatDateTime(value, {
    timezone,
    format: DATE_TIME_INPUT_FORMAT,
    showTimeZone: false,
  }) || null;

/**
 * Parses a Mantine DateTimePicker value from modal forms into a Luxon DateTime
 * in the site timezone. Use before persisting picker changes as ISO timestamps.
 */
export const parseDateTimeInputValue = (
  value: string,
  timezone?: string | null
) => {
  const trimmed = value.trim();
  const zone = resolveDateTimeZone(timezone);

  for (const format of DATE_TIME_INPUT_PARSE_FORMATS) {
    const parsed = DateTime.fromFormat(trimmed, format, { zone });
    if (parsed.isValid) return parsed;
  }

  return parseDateTime(trimmed.replace(' ', 'T'), {
    zone: timezone,
  });
};

/**
 * Parses the supported year-first date-only entry formats in the site timezone.
 * Keep this list intentionally narrow until user feedback identifies more
 * formats to support.
 */
export const parseDateOnlyInputValue = (
  value: string,
  timezone?: string | null
) => {
  const trimmed = value.trim();
  const zone = resolveDateTimeZone(timezone);

  for (const format of DATE_ONLY_INPUT_FORMATS) {
    const parsed = DateTime.fromFormat(trimmed, format, { zone });
    if (parsed.isValid && parsed.toFormat(format) === trimmed) return parsed;
  }

  return DateTime.invalid('invalid date-only input value');
};

/**
 * Formats the editable target used by claim service date-time controls.
 */
export const formatDateTimeEditorValue = (
  value: DateTimeInput,
  timezone?: string | null
) =>
  formatDateTimePattern(value, DATE_TIME_EDITOR_FORMAT, {
    timezone,
    showTimeZone: false,
  });

/**
 * Parses claim service date-time text. A date-only value keeps the time from
 * the currently committed value in the site timezone.
 */
export const parseDateTimeEditorValue = (
  value: string,
  currentValue: DateTimeInput,
  timezone?: string | null
) => {
  const trimmed = value.trim();
  const zone = resolveDateTimeZone(timezone);
  const parsedDate = parseDateOnlyInputValue(trimmed, zone);

  if (parsedDate.isValid) {
    const currentDateTime = parseInZone(currentValue, zone);

    if (!currentDateTime.isValid) {
      return DateTime.invalid('invalid date-time editor value');
    }

    return parsedDate.set({
      hour: currentDateTime.hour,
      minute: currentDateTime.minute,
      second: currentDateTime.second,
      millisecond: currentDateTime.millisecond,
    });
  }

  return parseDateTimeInputValue(trimmed, zone);
};

/**
 * Formats a date-time with a Luxon token pattern such as `yyyy-MM-dd HH:mm`.
 * Use when the UI or generated text requires a fixed token pattern instead of
 * a locale-aware label.
 */
export const formatDateTimePattern = (
  value: DateTimeInput,
  pattern: string,
  optionsOrTimezone?: DatePatternOptions | string | null
) =>
  formatDateTimeValue(value, optionsOrTimezone, (parsed, options) =>
    appendTimeZoneLabel(parsed.toFormat(pattern), parsed, options)
  );

/**
 * Formats a timestamp as an `HH:mm` string for Mantine TimePicker-style inputs.
 */
export const formatTimeInput = (
  value: DateTimeInput,
  timezone?: string | null
) => formatDateTimePattern(value, 'HH:mm', { timezone, showTimeZone: false });

/**
 * Formats a start/end calendar date pair as a user-facing range label.
 */
export const formatDateRange = (
  start: DateTimeInput,
  end: DateTimeInput,
  options?: DateDisplayOptions
) => {
  const fallback = options?.fallback ?? 'N/A';
  const startLabel = formatDateOnly(start, { ...options, fallback });
  const endLabel = formatDateOnly(end, { ...options, fallback });
  return `${startLabel} - ${endLabel}`;
};
